import { PromptTemplateType } from '@repo/shared/dtos/organization'
import { DataProvider } from '@repo/ui/dataProvider'
import { useEffect } from 'react'

import { PromptEditor } from '@/components/admin/PromptEditor'
import { SkeletonCard } from '@/components/skeletons/SkeletonCard'
import { RootState } from '@/store'
import { adminStoreActions } from '@/store/actions'
import { useFeatureFlags } from '@/store/feature-flags/hooks'
import { useStoreDispatch } from '@/store/hooks'

export function PromptTemplates({
  promptTemplateType,
  isDefaultOrganization,
  organizationId,
}: {
  promptTemplateType: PromptTemplateType
  isDefaultOrganization: boolean
  organizationId: string
}) {
  const dispatch = useStoreDispatch()
  useEffect(() => {
    void dispatch(adminStoreActions.promptTemplate.init())
  }, [])

  const { hasFeature } = useFeatureFlags()
  const data = getPromptTemplateData(promptTemplateType, hasFeature)

  if (!data || !data.isAllowed) return null
  return (
    <DataProvider
      loadingComponent={<SkeletonCard />}
      selectors={[(state: RootState) => state.admin.promptTemplates]}
      render={(promptTemplates) => {
        const promptTemplate = promptTemplates[promptTemplateType]
        if (!promptTemplate) return null
        return (
          <PromptEditor
            key={promptTemplate.id}
            onPromptUpdated={(promptText: string) => {
              void dispatch(
                adminStoreActions.promptTemplate.updateTemplate({
                  promptText,
                  promptTemplateType,
                }),
              )
            }}
            organizationId={organizationId}
            placeholder={data.placeholder}
            promptTemplate={promptTemplate}
            title={data.title}
            warn={isDefaultOrganization}
          />
        )
      }}
    />
  )
}

const getPromptTemplateData = (
  promptTemplateType: PromptTemplateType,
  hasFeature: ReturnType<typeof useFeatureFlags>['hasFeature'],
): {
  isAllowed: boolean
  placeholder: string
  title: string
} | null => {
  switch (promptTemplateType) {
    case PromptTemplateType.FILE_SUMMARY_SUGGEST:
      return {
        isAllowed: true,
        placeholder: 'Define the file summary prompt',
        title: "Prompt: File Summary (Beneficiary's Note)",
      }
    case PromptTemplateType.RESOURCES_FILE_LOAD:
      return {
        isAllowed: true,
        placeholder: 'Define the resource file prompt',
        title: 'Prompt: Resource File (Upload)',
      }
    case PromptTemplateType.BENEFICIARY_PROFILE_FILE_LOAD:
      return {
        isAllowed: true,
        placeholder: 'Define the beneficiary profile file prompt',
        title: 'Prompt: Beneficiary Profile File (Upload)',
      }
    case PromptTemplateType.BENEFICIARY_PROFILE:
      return {
        isAllowed: true,
        placeholder: 'Define the beneficiary profile prompt',
        title: 'Prompt: Beneficiary Profile',
      }
    case PromptTemplateType.BENEFICIARY_STRUCTURED_DATA:
      return {
        isAllowed: true,
        placeholder: 'Define the beneficiary structured data prompt',
        title: 'Prompt: Beneficiary Structured Data',
      }
    case PromptTemplateType.FILE_IMPORT:
      return {
        isAllowed: true,
        placeholder: 'Define the file import prompt',
        title: "Prompt: File Import (Document with user's instructions)",
      }
    case PromptTemplateType.ORGANIZATION_DESCRIPTION:
      return {
        isAllowed: true,
        placeholder: 'Define the Organization description onboarding prompt',
        title: 'Prompt: Organization Description (OnBoarding)',
      }
    case PromptTemplateType.ORGANIZATION_METHODOLOGY:
      return {
        isAllowed: true,
        placeholder: 'Define the Organization methodology onboarding prompt',
        title: 'Prompt: Organization Methology (OnBoarding)',
      }
    case PromptTemplateType.CASEWORKER_DASHBOARD_SUGGEST:
      return {
        isAllowed: true,
        placeholder: "Define the case worker's dashboard prompt",
        title: "Prompt: Case Worker's Dashboard",
      }
    case PromptTemplateType.CHAT_CONVERSE:
      hasFeature('emailAnalysis')
      return {
        isAllowed: hasFeature('chat'),
        placeholder: "Define the chat's answers prompt",
        title: "Prompt: Chat's Answers",
      }
    case PromptTemplateType.CHAT_WELCOME:
      return {
        isAllowed: hasFeature('chat'),
        placeholder: "Define the chat's welcome message prompt",
        title: "Prompt: Chat's Welcome Message",
      }

    default:
      return null
  }
}
